import { useState, useEffect, useCallback } from 'react';
import { PopupForm, PopupFormData } from '../components/PopupForm';

interface Popup {
  id: string;
  title: string;
  body_content: string;
  cta_text: string;
  cta_url: string;
  target_company_id: string | null;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
}

interface Company {
  id: string;
  usual_name: string;
}

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    },
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message ?? `Request failed with status ${res.status}`);
  }
  return res.json();
}

export function PopupsPage() {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPopup, setEditingPopup] = useState<Popup | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadPopups = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchJson<{ data: Popup[] } | Popup[]>(`${API_BASE}/admin/popups`);
      const data = Array.isArray(response) ? response : (response as { data: Popup[] }).data ?? [];
      setPopups(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load popups');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const response = await fetchJson<{ data: Company[] } | Company[]>(`${API_BASE}/admin/companies`);
      const data = Array.isArray(response) ? response : (response as { data: Company[] }).data ?? [];
      setCompanies(data);
    } catch {
      // Companies loading failure is non-critical; dropdown will be empty
    }
  }, []);

  useEffect(() => {
    loadPopups();
    loadCompanies();
  }, [loadPopups, loadCompanies]);

  async function handleCreate(data: PopupFormData) {
    try {
      setIsSaving(true);
      await fetchJson(`${API_BASE}/admin/popups`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      setShowForm(false);
      await loadPopups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create popup');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleUpdate(data: PopupFormData) {
    if (!editingPopup) return;
    try {
      setIsSaving(true);
      await fetchJson(`${API_BASE}/admin/popups/${editingPopup.id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      setEditingPopup(null);
      setShowForm(false);
      await loadPopups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update popup');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(popup: Popup) {
    if (!confirm(`Are you sure you want to delete the popup "${popup.title}"?`)) {
      return;
    }
    try {
      await fetchJson(`${API_BASE}/admin/popups/${popup.id}`, {
        method: 'DELETE',
      });
      await loadPopups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete popup');
    }
  }

  async function handleToggleActive(popup: Popup) {
    try {
      const action = popup.is_active ? 'deactivate' : 'activate';
      await fetchJson(`${API_BASE}/admin/popups/${popup.id}/${action}`, {
        method: 'POST',
      });
      await loadPopups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle popup status');
    }
  }

  function handleEdit(popup: Popup) {
    setEditingPopup(popup);
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingPopup(null);
  }

  function getCompanyName(companyId: string | null): string {
    if (!companyId) return 'All clients';
    const company = companies.find((c) => c.id === companyId);
    return company?.usual_name ?? 'Unknown';
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (showForm) {
    const formInitialData = editingPopup
      ? {
          title: editingPopup.title,
          body_content: editingPopup.body_content,
          cta_text: editingPopup.cta_text,
          cta_url: editingPopup.cta_url,
          target_company_id: editingPopup.target_company_id,
          start_date: editingPopup.start_date
            ? editingPopup.start_date.slice(0, 16)
            : '',
          end_date: editingPopup.end_date
            ? editingPopup.end_date.slice(0, 16)
            : '',
        }
      : undefined;

    return (
      <div>
        <div className="page-header">
          <h1>{editingPopup ? 'Edit Popup' : 'Create Popup'}</h1>
          <p>{editingPopup ? 'Update popup details.' : 'Configure a new popup announcement.'}</p>
        </div>
        <PopupForm
          initialData={formInitialData}
          companies={companies}
          onSubmit={editingPopup ? handleUpdate : handleCreate}
          onCancel={handleCancel}
          isLoading={isSaving}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header__row">
          <div>
            <h1>Popups</h1>
            <p>Create and manage dynamic popup announcements.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + New Popup
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
          <button
            className="alert__dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">Loading popups...</div>
      ) : popups.length === 0 ? (
        <div className="empty-state">
          <p>No popups created yet.</p>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            Create your first popup
          </button>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table" aria-label="Popups list">
            <thead>
              <tr>
                <th>Status</th>
                <th>Title</th>
                <th>Target</th>
                <th>Start</th>
                <th>End</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {popups.map((popup) => (
                <tr key={popup.id}>
                  <td>
                    <span
                      className={`badge ${popup.is_active ? 'badge--success' : 'badge--inactive'}`}
                    >
                      {popup.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{popup.title}</td>
                  <td>{getCompanyName(popup.target_company_id)}</td>
                  <td>{formatDate(popup.start_date)}</td>
                  <td>{formatDate(popup.end_date)}</td>
                  <td className="data-table__actions">
                    <button
                      className={`btn btn-sm ${popup.is_active ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleActive(popup)}
                      title={popup.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {popup.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(popup)}
                      title="Edit popup"
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(popup)}
                      title="Delete popup"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
